import React, { useState } from "react";
import { Loader2, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { auth, normalizeError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import NumaMascot from "@/components/NumaMascot";
import { toast } from "sonner";

/**
 * The screen between registering and using NUMA.
 *
 * It exists so that a mistyped address is a five-second correction rather than
 * an account nobody can ever reach again - no password reset, no reply to a
 * support request, nothing. Which means this screen has to say exactly what to
 * do, including the two things that actually go wrong: the message lands in
 * spam, or the address was wrong.
 */
export default function PendingVerification() {
  const { user, logout, checkAuth } = useAuth();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  const resend = async () => {
    setSending(true);
    try {
      const res = await auth.resendVerification();
      if (res.already_verified) {
        await checkAuth();
        return;
      }
      toast[res.sent ? "success" : "info"](
        res.sent
          ? `Messaggio inviato di nuovo a ${res.email}`
          : "Non è stato possibile inviare il messaggio adesso. Riprova fra poco."
      );
    } catch (err) {
      const e = normalizeError(err);
      toast.error(e.message, { description: e.action || undefined });
    } finally {
      setSending(false);
    }
  };

  const recheck = async () => {
    setChecking(true);
    try {
      await checkAuth();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 py-12"
         data-testid="pending-verification">
      <div className="w-full max-w-[440px] text-center">
        <NumaMascot size={64} className="numa-mark mx-auto" variant={4} />
        <h1 className="font-serif-display mt-5 text-2xl font-semibold text-ink">
          Controlla la tua posta
        </h1>
        <p className="mt-2.5 text-[14px] leading-relaxed text-ink-muted">
          Abbiamo inviato un link di conferma a{" "}
          <strong className="text-ink">{user?.email}</strong>. Aprilo per iniziare a
          usare NUMA: serve a essere sicuri di poterti ricontattare.
        </p>

        <div className="mt-6 space-y-2.5 rounded-md border border-line bg-surface px-4 py-4 text-left">
          <p className="flex items-start gap-2 text-[13px] leading-relaxed text-ink-muted">
            <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" />
            Il messaggio arriva entro un paio di minuti. Se non lo vedi, controlla la
            posta indesiderata: è la ragione più comune.
          </p>
          <p className="text-[13px] leading-relaxed text-ink-muted">
            Il link resta valido 48 ore.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Button onClick={recheck} disabled={checking} data-testid="recheck-verification">
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Ho confermato, continua
          </Button>
          <Button variant="outline" onClick={resend} disabled={sending} data-testid="resend-verification">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Invia di nuovo
          </Button>
        </div>

        <p className="numa-meta mt-6 leading-relaxed">
          Hai sbagliato indirizzo?{" "}
          <button type="button" onClick={logout} className="font-medium text-brand hover:underline">
            Esci e registrati con un altro
          </button>
        </p>
      </div>
    </div>
  );
}
